import Vue from 'vue'
import VueRouter from 'vue-router'
import store from '@/core/store'
import i18n from '@/core/utils/I18n'
import routes from '@/core/router/routes'
import { isEqual } from 'lodash'
import { projectRoute } from '@/core/helpers'
import { $hasFeature, $hasProviderFeature } from '@/core/utils/VueAcl'
Vue.use(VueRouter)

const router = new VueRouter({
  mode: 'history',
  routes: routes
})

router.beforeEach(async function(to, from, next) {
  if (from.name) {
    // Check above is to avoid redirecting on initial page load
    if (from.meta.vue === false || to.meta.vue === false) {
      if (!to.hash || from.path !== to.path || !isEqual(from.query, to.query)) {
        window.location.href = to.fullPath
      }
      return
    }
  }

  if (to.meta.project !== false) {
    if (!to.query.project_id) {
      return next(notFoundPage(to))
    }

    if (Number(to.query.project_id) !== Number(store.getters.projectId)) {
      await fetchUserAndProject(to, next)
    }
  } else {
    store.commit('setProjectConfig', {})
    store.commit('setUserPreferences', {})
  }

  if (to.meta.auth) {
    if (!checkPermissions(to)) {
      next(forbiddenPage(to))
    }
  }

  if (to.meta.projectFeatures) {
    if (!checkProjectFeatures(to)) {
      if (to.meta.projectFeatures.notFound) {
        next(notFoundPage(to))
      } else {
        next(forbiddenPage(to))
      }
    }
  }

  if (to.meta.providerFeatures) {
    if (!checkProviderFeatures(to)) {
      if (to.meta.providerFeatures.notFound) {
        next(notFoundPage(to))
      } else {
        next(forbiddenPage(to))
      }
    }
  }

  if (to.meta.features) {
    if (!checkFeatures(to)) {
      next(forbiddenPage(to))
    }
  }

  if (to.meta.title) {
    document.title = i18n.t(to.meta.title)
  }

  // @TODO: Remove after Campaign create release
  if (
    [
      'UpdateCampaign',
      'CopyCampaign',
      'ViewCampaign',
      'CreateCampaign'
    ].includes(to.name) &&
    store.state.app.features.campaignLegacyUI
  ) {
    let route = router.resolve(projectRoute({ name: `${to.name}Old` })).route
    window.location.href = to.fullPath.replace(to.path, route.path)
    return
  }

  if (to.meta.hasOwnProperty('action')) {
    var doAction = false
    if (to.meta.hasOwnProperty('avoid_action_query_params')) {
      to.meta.avoid_action_query_params.forEach(key => {
        if (!to.query.hasOwnProperty(key)) {
          doAction = true
        }
      })
    }
    if (doAction) {
      to.meta.action(to)
      return
    }
  }

  next()
})

if (window.self !== window.top) {
  router.afterEach(to => {
    let message = {
      type: 'path',
      path: to.fullPath,
      title: document.title
    }

    window.parent.postMessage(message, '*')
  })

  window.addEventListener('message', event => {
    if (event.data && event.data.type === 'path') {
      let url = new URL(window.origin + event.data.path)
      let newParams = new URLSearchParams(url.search)
      let params = new URLSearchParams(window.location.search)

      if (
        url.pathname === window.location.pathname &&
        params.has('project_id') &&
        newParams.has('project_id') &&
        params.get('project_id') !== newParams.get('project_id')
      ) {
        window.location = event.data.path
      }

      router.push(event.data.path)
    }
  })
}

const checkPermissionOrRole = item => {
  return (
    store.getters.permissions.includes(item) ||
    store.state.app.user.role === item
  )
}

const checkPermissions = route => {
  for (const item of route.meta.auth || []) {
    if (item.startsWith('!') && checkPermissionOrRole(item.replace('!', ''))) {
      return false
    }
  }

  return route.meta.auth.find(role => checkPermissionOrRole(role))
}

const checkFeatures = route => {
  for (const item of route.meta.features || []) {
    if (!store.state.app.features[item]) {
      return false
    }
  }
  return true
}

const checkProjectFeatures = route => {
  const projectFeatures = route.meta.projectFeatures

  if (Array.isArray(projectFeatures)) {
    return projectFeatures.every(checkProjectFeature)
  } else if (typeof projectFeatures === 'object') {
    const items = projectFeatures.items
    if (Array.isArray(items)) {
      return items.every(checkProjectFeature)
    }
  }

  return false
}

const checkProjectFeature = feature => {
  let invert = false
  if (feature.startsWith('!')) {
    invert = true
    feature = feature.slice(1)
  }

  const result = $hasFeature(feature)
  return invert ? !result : result
}

const checkProviderFeatures = route => {
  const providerFeatures = route.meta.providerFeatures

  if (Array.isArray(providerFeatures)) {
    return providerFeatures.every(checkProviderFeature)
  } else if (typeof providerFeatures === 'object') {
    const items = providerFeatures.items
    if (Array.isArray(items)) {
      return items.every(checkProviderFeature)
    }
  }

  return false
}

const checkProviderFeature = feature => {
  let invert = false
  if (feature.startsWith('!')) {
    invert = true
    feature = feature.slice(1)
  }

  const result = $hasProviderFeature(feature)
  return invert ? !result : result
}

const notFoundPage = route => {
  return {
    name: 'NotFound',
    params: [route.path],
    query: route.query,
    replace: true
  }
}

const forbiddenPage = route => {
  return {
    name: 'Forbidden',
    params: [route.path],
    query: route.query,
    replace: true
  }
}

const restErrorPage = (route, error) => {
  return {
    name: 'RestError',
    params: [route.path, error],
    query: route.query,
    replace: true
  }
}

const fetchUserAndProject = async (to, next) => {
  try {
    await store.dispatch('fetchProjectConfig', to.query.project_id)
    await store.dispatch('fetchUserPreferences', to.query.project_id)
    await store.dispatch('refreshPlatformChannels', to.query.project_id)
  } catch (error) {
    return next(restErrorPage(to, error))
  }
}

export default router



// WEBPACK FOOTER //
// ./src/core/router/index.js